import { TournamentType } from '@bg-components/Profile/enums';
import { IHowToPlaySection } from '@bg-shared/business/HowToPlay';
import { IIntroPageData } from '../../../../../components/src/components/Profile/interfaces';
import { IGamificationModel, INotificationMessage } from '../interfaces';
import { getTournamentPriority } from '../utils';
import { IAcquiredAssets, ICUAsset } from '../services/CaptainUpAPI';
import { INTRO_PAGE_ORDERED_PRESET } from '../constants';

export const gamificationSelectors = {
    hasError: (state: IGamificationModel): boolean => state.settings.error,

    settingsLoaded: (state: IGamificationModel): boolean => state.settings.settingsLoaded,

    isLoaded: (state: IGamificationModel): boolean => state.settings.loaded,

    isBlocked: (state: IGamificationModel): boolean => state.profile.blocked,

    hasAccount: (state: IGamificationModel): boolean => !!state.profile.userInfo.id,

    getUserInfo: (state: IGamificationModel): IGamificationModel['profile']['userInfo'] =>
        state.profile.userInfo,

    getInboxes: (state: IGamificationModel): IGamificationModel['inboxes'] => state.inboxes,

    getPlayerName: (state: IGamificationModel) => state.profile.userInfo.name,

    getAchievedBadges: (state: IGamificationModel): IGamificationModel['badges']['achieved'] =>
        state.badges.achieved,

    getChallengesBadges: (state: IGamificationModel): IGamificationModel['badges']['challenges'] =>
        state.badges.challenges,

    getUnSeenInboxes: (state: IGamificationModel): IGamificationModel['inboxes'] =>
        state.inboxes.filter((inbox) => inbox.unseen),

    getUnSeenAchievements: (state: IGamificationModel): IGamificationModel['badges']['achieved'] =>
        state.badges.achieved.filter((inbox) => inbox.unseen),

    getUnreadInboxesCount: (state: IGamificationModel): number =>
        state.inboxes.filter((inbox) => inbox.unread).length,

    getUnreadAchievedBadgesCount: (state: IGamificationModel): number =>
        state.badges.achieved.filter((achievement) => achievement.unread).length,

    getUnreadChallengesBadgesCount: (state: IGamificationModel): number =>
        state.badges.challenges.filter((badge) => badge.unread).length,

    getLevel: (state: IGamificationModel): IGamificationModel['profile']['level'] =>
        state.profile.level,

    getShownNotifications: (state: IGamificationModel): string[] =>
        state.notifications.shownNotifications,

    getOpenedWizard: (state: IGamificationModel): boolean => state.settings.openedWizard,

    getNotificationMessage: (state: IGamificationModel): INotificationMessage =>
        state.notifications.notificationMessage,

    getTournamentRewards: (tournamentId: string) => (state: IGamificationModel) =>
        state.tournaments.list.find((item) => item.id === tournamentId)?.rewards ?? [],

    getTournaments: (state: IGamificationModel) => {
        const currentDate = new Date();
        const sorted = [...state.tournaments.list].sort((a, b) => {
            const aActiveFrom = new Date(a.activeFromDate);
            const aDisabledFrom = new Date(a.disabledFromDate);
            const bActiveFrom = new Date(b.activeFromDate);
            const bDisabledFrom = new Date(b.disabledFromDate);

            const aPriority = getTournamentPriority(currentDate, aActiveFrom, aDisabledFrom);
            const bPriority = getTournamentPriority(currentDate, bActiveFrom, bDisabledFrom);

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            if (aPriority === TournamentType.ONGOING) {
                return aActiveFrom.getTime() - bActiveFrom.getTime();
            }

            if (aPriority === TournamentType.UPCOMING) {
                return aActiveFrom.getTime() - bActiveFrom.getTime();
            }

            return bDisabledFrom.getTime() - aDisabledFrom.getTime();
        });

        return {
            tournaments: sorted,
            optInIds: state.tournaments.optInIds,
        };
    },

    getShopItems: (state: IGamificationModel): ICUAsset[] => state.shopItems,

    getAcquiredItems: (state: IGamificationModel): IAcquiredAssets[] => state.acquiredItems,

    getIntroPageTranslations: (state: IGamificationModel): IHowToPlaySection[] =>
        state.introPageTranslations?.blocks || [],

    getIntroTabBlocks: (state: IGamificationModel): IIntroPageData[] => {
        const data = gamificationSelectors.getIntroPageTranslations(state);
        return data.slice(0, 4).map((item, index) => ({
            ...item,
            ...INTRO_PAGE_ORDERED_PRESET[index],
        }));
    },

    getTermsAndConditionsBlock: (state: IGamificationModel): IHowToPlaySection => {
        const data = gamificationSelectors.getIntroPageTranslations(state);
        return data[4];
    },
};
